import type { TaskChipInfo } from "@justone/editor";
import {
  dateParts,
  daysBetween,
  describeRecurrence,
  nowInZone,
  parseRRule,
  TASK_STATUS_NAMES,
  type TaskDTO,
  type TaskFilter,
  type TaskRefDTO,
  type UserDTO,
  timeToMinutes,
  todayInZone,
  WEEKDAY_NAMES,
  weekday,
} from "@justone/shared";

export const today = () => todayInZone();

/** 저장 값은 1(가장 중요)~4(없음). 숫자는 작을수록 중요해 헷갈리므로 화면에는 말로 보인다 */
export const PRIORITY_LABELS: Record<number, string> = { 1: "높음", 2: "중간", 3: "낮음", 4: "없음" };
export const PRIORITY_NAMES: Record<number, string> = { 1: "우선순위 높음", 2: "우선순위 중간", 3: "우선순위 낮음", 4: "우선순위 없음" };

/** "오후 3시", "오후 3:30" */
export function formatClock(time: string): string {
  const [h, m] = time.split(":").map(Number) as [number, number];
  const period = h < 12 ? "오전" : "오후";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${period} ${h12}${m ? `:${String(m).padStart(2, "0")}` : "시"}`;
}

/** 날짜를 오늘 기준 말로: 오늘, 내일, 모레, 어제, 일주일 안이면 요일, 그 밖에는 "10월 20일" */
export function formatDueDate(date: string, base = today()): string {
  const diff = daysBetween(base, date);
  if (diff === 0) return "오늘";
  if (diff === 1) return "내일";
  if (diff === 2) return "모레";
  if (diff === -1) return "어제";
  if (diff > 1 && diff < 7) return `${WEEKDAY_NAMES[weekday(date)]}요일`;
  const { year, month, day } = dateParts(date);
  const y = dateParts(base).year;
  return year === y ? `${month}월 ${day}일` : `${year}년 ${month}월 ${day}일`;
}

/** "10월 20일 (화)" — 다가오는 화면 머리글 */
export function formatDayHeading(date: string, base = today()): string {
  const { month, day } = dateParts(date);
  const diff = daysBetween(base, date);
  const rel = diff === 0 ? " · 오늘" : diff === 1 ? " · 내일" : "";
  return `${month}월 ${day}일 (${WEEKDAY_NAMES[weekday(date)]})${rel}`;
}

export function formatDue(t: Pick<TaskDTO, "dueDate" | "dueTime">): string {
  if (!t.dueDate) return "";
  return t.dueTime ? `${formatDueDate(t.dueDate)} ${formatClock(t.dueTime)}` : formatDueDate(t.dueDate);
}

export type DueTone = "overdue" | "today" | "soon" | "later";

/** 지난 마감(빨강), 오늘(초록), 내일(노랑), 그 뒤 */
export function dueTone(t: Pick<TaskDTO, "dueDate" | "dueTime" | "status">): DueTone | null {
  if (!t.dueDate) return null;
  const now = nowInZone();
  const diff = daysBetween(now.date, t.dueDate);
  if (t.status !== "done" && (diff < 0 || (diff === 0 && t.dueTime && timeToMinutes(t.dueTime) < now.minutes))) return "overdue";
  if (diff === 0) return "today";
  if (diff === 1) return "soon";
  return "later";
}

export function recurrenceText(t: Pick<TaskDTO, "rrule" | "repeatFromCompletion">): string | null {
  if (!t.rrule) return null;
  const r = parseRRule(t.rrule);
  return r ? describeRecurrence(r, t.repeatFromCompletion) : null;
}

/** 마감 순 (날짜 없는 것은 뒤로), 같으면 우선순위, 놓은 순서 */
export function byDue(a: TaskDTO, b: TaskDTO): number {
  const ad = a.dueDate ?? "9999-12-31";
  const bd = b.dueDate ?? "9999-12-31";
  if (ad !== bd) return ad < bd ? -1 : 1;
  const at = a.dueTime ?? "99:99";
  const bt = b.dueTime ?? "99:99";
  if (at !== bt) return at < bt ? -1 : 1;
  return a.priority - b.priority || a.position - b.position || a.id - b.id;
}

/* ───────────── 보드 필터 (화면에서 거른다) ───────────── */

export interface BoardFilter {
  assignee: "" | "me" | "none" | number;
  labelId: number | "";
  due: "" | "overdue" | "today" | "this_week" | "none";
  priority: number | "";
}

export const EMPTY_BOARD_FILTER: BoardFilter = { assignee: "", labelId: "", due: "", priority: "" };

export function matchesBoardFilter(t: TaskDTO, f: BoardFilter, meId: number): boolean {
  if (f.assignee === "me" && t.assigneeId !== meId) return false;
  if (f.assignee === "none" && t.assigneeId !== null) return false;
  if (typeof f.assignee === "number" && t.assigneeId !== f.assignee) return false;
  if (f.labelId !== "" && !t.labelIds.includes(f.labelId)) return false;
  if (f.priority !== "" && t.priority !== f.priority) return false;
  if (f.due) {
    const d = today();
    if (f.due === "none") return t.dueDate === null;
    if (!t.dueDate) return false;
    const diff = daysBetween(d, t.dueDate);
    if (f.due === "overdue") return diff < 0 && t.status !== "done";
    if (f.due === "today") return diff === 0;
    if (f.due === "this_week") return diff >= 0 && diff <= 6 - weekday(d);
  }
  return true;
}

/* ───────────── 개인 화면 조건 ───────────── */

export { VIEW_FILTERS } from "@justone/shared";

/** 끌어서 놓은 자리의 순서 값: 앞뒤 할일 사이 */
export function positionBetween(before: TaskDTO | undefined, after: TaskDTO | undefined): number {
  if (before && after) return (before.position + after.position) / 2;
  if (before) return before.position + 1;
  if (after) return after.position - 1;
  return 1;
}

/** 할일 상세 주소: 지금 화면에 ?task=를 붙인다 */
export function withTask(search: string, id: number | null): string {
  const p = new URLSearchParams(search);
  if (id === null) p.delete("task");
  else p.set("task", String(id));
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** `#T-123` 칩 내용 (메시지와 위키 편집기가 같이 쓴다). 할일 상태는 진행중·완료만 적는다 */
export function taskChipInfo(t: TaskRefDTO, users: Map<number, UserDTO>): TaskChipInfo {
  const tone = dueTone(t);
  return {
    title: t.title,
    status: t.status,
    statusLabel: t.status === "todo" ? "" : TASK_STATUS_NAMES[t.status],
    assignee: t.assigneeId !== null ? (users.get(t.assigneeId)?.displayName ?? null) : null,
    due: t.dueDate ? formatDue(t) : null,
    dueTone: tone === "later" ? null : tone,
    deleted: t.deleted,
  };
}
